import { CircularProgress, Grid, InputLabel, TextField, Tooltip, Typography } from "@material-ui/core";
import {
  useConfirmation,
  IconButton,
  FilterRequire,
  CoolSwitch,
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  RewindIcon,
  PreviousIcon,
  NextIcon,
  StepBackIcon,
  StepForwardIcon,
} from '@coolremote/shared';
import Slider from "@material-ui/core/Slider";
import ArrowBack from "@material-ui/icons/ArrowBack";
import ArrowRight from "@material-ui/icons/ArrowForward";
import { Autocomplete } from "@material-ui/lab";
import clsx from "clsx";
import { System as sdkSystem } from "coolremote-sdk";
import _, { isEmpty } from "lodash";
import moment from "moment";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { t } from "ttag";
import Header from "../../components/Header/Header";
import Loading from "../../components/Loading/Loading";
import ServiceNavigationBar from "../../components/Menu/ServiceNavigationBar";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { MenuSearch as Search } from "../../svgComponents";
import ParamsTable from "./ParamsTable";
import PropsFilter from "./PropsFilter";
import useStyles from "./SystemDiagnostics.style";
import { isSameDay } from "date-fns";
import { getUtcRange } from "@services/timeService";
import {
  consumeSysDiagJumpContext,
  ISysDiagJumpContext
} from "@services/sysDiagJumpService";
const AUTOUPDATE_INTERVAL = 1000 * 15; // one minute / 4

interface IProps {
  children: React.ReactElement;
  open: boolean;
  value: any;
}
function ValueLabelComponent(props: IProps) {
  const classes = useStyles();
  const { children, open, value } = props;

  return (
    <Tooltip
      arrow
      open={open}
      enterTouchDelay={0}
      placement="top"
      title={value}
      classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow, popper: classes.tooltipPopper }}
    >
      {children}
    </Tooltip>
  );
}

const paramsReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'all':
      return {
        indoors: action.indoors,
        outdoors: action.outdoors,
        bsBoxes: action.bsBoxes,
        indoorsObj: action.indoorsObj,
        outdoorsObj: action.outdoorsObj,
        bsBoxesObj: action.bsBoxesObj,
      };

    case 'selectUnSelect': {
      const { code, value, flag, paramsType, updateAPI, systemId } = action;
      const paramsObj = { ...state[paramsType + 'Obj'] };
      let paramsOrder = [...state[paramsType]].map((param) => {
        //force all param codes to be strings.
        param[0] = param[0] + '';
        return param;
      });
      let count = 0;

      if (code === 'all' && flag === 'showInList') {
        Object.values(paramsObj).forEach((param: any) => {
          param.showInList = value;
          if (value && count < 9) {
            param.showInTable = true;
            count++;
          } else {
            param.showInTable = false;
          }
        });

        paramsOrder = paramsOrder.map((param: any) => {
          const code = param[0] + '';
          if (!value) {
            return [code, false, false];
          }
          const paramObj = paramsObj[code];
          return [code, paramObj ? paramObj.showInTable : false, true];
        });

        updateAPI({ type: paramsType, systemId, paramsOrder });
        return {
          ...state,
          [paramsType + 'Obj']: paramsObj,
          [paramsType]: paramsOrder,
        };
      } else if (flag === 'showInList' && code) {
        if (!value) {
          paramsObj[code].showInList = false;
          paramsObj[code].showInTable = false;
          for (let param of paramsOrder) {
            if (param[0] === code + '') {
              param[1] = false;
              param[2] = false;
              break;
            }
          }
        } else {
          Object.values(paramsObj).forEach(({ showInTable }: any) => {
            if (showInTable) {
              count++;
            }
          });

          paramsObj[code].showInList = true;
          if (count > 8) {
            paramsObj[code].showInTable = false;
          } else {
            paramsObj[code].showInTable = true;
          }

          for (let param of paramsOrder) {
            if (param[0] === code + '') {
              param[1] = count > 8 ? false : true;
              param[2] = true;
              break;
            }
          }
        }

        updateAPI({ type: paramsType, systemId, paramsOrder });
        return {
          ...state,
          [paramsType + 'Obj']: paramsObj,
          [paramsType]: paramsOrder,
        };
      } else if (flag === 'showInTable' && code) {
        if (!value) {
          paramsObj[code].showInTable = false;
          for (let param of paramsOrder) {
            if (param[0] === code + '') {
              param[1] = false;
              break;
            }
          }
        } else {
          //check if already 12 params are selected if yes do nothing
          Object.values(paramsObj).forEach(({ showInTable }: any) => {
            if (showInTable) {
              count++;
            }
          });

          if (count < 12) {
            paramsObj[code].showInTable = true;
            for (let param of paramsOrder) {
              if (param[0] === code + '') {
                param[1] = true;
                break;
              }
            }
          } else {
            return state;
          }
        }

        updateAPI({ type: paramsType, systemId, paramsOrder });
        return {
          ...state,
          [paramsType + 'Obj']: paramsObj,
          [paramsType]: paramsOrder,
        };
      }
      return state;
    }

    case 'reorder': {
      const { paramsType, updateAPI, systemId, paramsOrder = [] } = action;
      updateAPI({ type: paramsType, systemId, paramsOrder });
      return { ...state, [paramsType]: paramsOrder };
    }
    default:
      return state;
  }
};

const SystemDiagnostics: React.FC = (props: any) => {
  const classes = useStyles();
  const confirm = useConfirmation();
  const match = useRouteMatch<{ systemId: string }>();
  const history = useHistory();
  const defaultSystemId = match.params.systemId;
  const jumpContextRef = useRef<ISysDiagJumpContext | null | undefined>(
    undefined
  );
  if (jumpContextRef.current === undefined) {
    jumpContextRef.current = consumeSysDiagJumpContext(defaultSystemId);
  }
  const [highlightUnitId, setHighlightUnitId] = useState<string | null>(null);
  const user = useStoreState((s) => s.users.me);
  const isInitialized = useStoreState((s) => s.isInitialized);
  const selections = useStoreState((s) => s.selections.selections);
  const allSystems = useStoreState((s) => s.systems.allSystems);
  const allUnits = useStoreState((state) => state.units.allUnits);
  const allDevices = useStoreState((s) => s.devices.allDevices);
  const allSites = useStoreState((state) => state.sites.allSites);
  const types = useStoreState((state) => state.types);
  const setUnitUpdateStatus = useStoreActions(
    (action) => action.setUnitUpdateStatus
  );
  const updateSelections = useStoreActions(
    (a) => a.selections.updateSelections
  );
  const serviceParams = useStoreState((s) => s.serviceParams);
  const serviceParamTypes = useStoreState((s) => s.serviceParamTypes);
  const displayFlagsMap = useStoreState((state) => state.users.displayFlagsMap);
  const { customerId } = selections;
  const displayFlags = displayFlagsMap[customerId || ''] || {};
  const getSite = useStoreState((s) => s.sites.getSite);
  const { timeFormat } = useStoreState((state) => state.users);
  const getUserStorePreferences = useStoreActions(
    (a) => a.users.getUserStorePreferences
  );
  const updateUserSystemPreferences = useStoreActions(
    (a) => a.users.updateUserSystemPreferences
  );
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const setSelections = useStoreActions((s) => s.selections.setSelections);
  const sitesFlags = useStoreState((s) => s.sites.sitesFlags);
  const { siteId } = selections;

  const [isLoading, setIsLoading] = useState(true);

  const [time, setTime] = useState<any>({
    start: moment(new Date(new Date().setHours(0, 0, 0))).valueOf(),
    end: moment.now(),
  });
  const [selectedTime, setSelectedTime] = useState<any>(null);
  const [indoorData, setIndoorData] = useState<any>({});
  const [outdoorData, setOutdoorData] = useState<any>({});
  const [bsData, setBsData] = useState<any>({});
  const [isLast, setIsLast] = useState<boolean>(false);
  const [allObj, setAllObj] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [autoupdate, setAutoupdate] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const [callTimer, setCallTimer] = useState<any>(null);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [seekDir, setSeekDir] = useState<'fwd' | 'bwd' | null>(null);
  const [seekMult, setSeekMult] = useState<number>(1);

  const isPlayingRef = useRef(false);
  const seekDirRef = useRef<'fwd' | 'bwd' | null>(null);
  const seekMultRef = useRef(1);
  const selectedTimeRef = useRef<any>(null);
  const timeRef = useRef(time);
  const lastTsRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const autoupdateGateRef = useRef(false);
  const pendingDragValueRef = useRef<any>(null);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    seekDirRef.current = seekDir;
  }, [seekDir]);

  useEffect(() => {
    seekMultRef.current = seekMult;
  }, [seekMult]);

  useEffect(() => {
    selectedTimeRef.current = selectedTime;
  }, [selectedTime]);

  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  const [systemParams, setSystemParams] = useReducer(paramsReducer, {
    indoors: [],
    outdoors: [],
    bsBoxes: [],
    indoorsObj: {},
    outdoorsObj: {},
    bsBoxesObj: {},
  });

  const {
    temperatureScale: userTempScale = 1,
    measurementUnits: userPressureScale = 2,
  } = user;
  const timezone = getSite(selections.siteId)?.timezone || moment.tz.guess();
  const selectedSystemId = selections?.systemId;
  const system: any = selectedSystemId && allSystems[selectedSystemId];
  const device =
    selectedSystemId && system?.device && allDevices[system.device];
  const { unitTypes } = types;
  const deviceRegDate = device?.registrationDate || new Date('1-1-1900');

  const timeLimit = sitesFlags[siteId || '']?.numOfHourLimitSysDiag || 0;
  const pastTimeAllowed = new Date().getTime() - timeLimit * 60 * 60 * 1000;

  useEffect(() => {
    const currDate = new Date();
    // A "day" jumped in without its own value falls back to the timestamp's
    // calendar day — fetchData needs a day to know what to load at all.
    const jumpDay = jumpContextRef.current?.day || jumpContextRef.current?.timestamp;
    let date = jumpDay ? new Date(jumpDay) : selections.dateRange?.endDate || currDate;
    const allowedPastTime = currDate.getTime() - timeLimit * 60 * 60 * 1000;
    date =
      !!timeLimit && date.getTime() < allowedPastTime
        ? new Date(allowedPastTime)
        : date;
    updateSelections({
      type: 'time',
      data: { endDate: date, startDate: date },
    });
  }, [siteId]);

  useEffect(() => {
    if (!selectedSystemId) {
      setIsLoading(false);
      return;
    }

    if (allSystems[selectedSystemId]) {
      fetchData(selections.dateRange?.startDate, selectedSystemId);
    }
  }, [userTempScale, userPressureScale, selectedSystemId]);

  const onDateChange = (range: any) => {
    fetchData(range?.startDate, selectedSystemId);
  };

  useEffect(() => {
    if (
      selections?.dateRange?.endDate &&
      new Date(selections?.dateRange?.endDate) < new Date(deviceRegDate)
    ) {
      setSelections({
        dateRange: {
          startDate: new Date(new Date().setHours(0, 0, 0)),
          endDate: new Date(),
        },
      });
    }
  }, [deviceRegDate]);

  useEffect(() => {
    if (defaultSystemId && defaultSystemId !== selections.systemId) {
      updateSelections({ type: 'system', data: defaultSystemId });
      return;
    }

    if (!defaultSystemId && selections.systemId) {
      history.push(`/system-diagnostics/${selections.systemId}`);
      return;
    }
  }, [defaultSystemId]);

  useEffect(() => {
    history.push(
      `/system-diagnostics/${selections.systemId ? selections.systemId : ''}`
    );
  }, [selections.systemId]);

  const getParamsOrder = (userParamsOrder: any, typeParamsKeys: any) => {
    let newParamsOrder: any = [];
    const paramsObject: any = {};
    let count = 0;

    if (typeParamsKeys.length === 0) {
      return { newParamsOrder: [], paramsObject: {} };
    }

    if (!userParamsOrder) {
      const showInList = true;
      let showInTable = true;
      typeParamsKeys.forEach((key: string) => {
        const serviceParam = serviceParams[key];
        if (!serviceParam) {
          return;
        }
        const {
          showInGraph = true,
          showInSysDiag = true,
          isCustomParam = false,
          code,
        } = serviceParam;
        if (!showInSysDiag) {
          return;
        }
        const measurementUnits: string = serviceParam
          ? serviceParam.data_unit_of_measurement
          : '';

        const name: string = serviceParam ? serviceParam.title : '';

        let enumData = null;
        if (serviceParam?.enum) {
          const typeKey = serviceParam?.enum;
          enumData = serviceParamTypes[typeKey];
        }

        if (showInTable && count === 9) {
          showInTable = false;
        }
        count < 9 && count++;

        newParamsOrder.push([key, showInTable, showInList]);
        paramsObject[key] = {
          code,
          name,
          // value: paramsMap[code].value,
          showInGraph,
          measurementUnits,
          enumData,
          showInList,
          showInTable,
          techInfo: serviceParam?.techInfo || null,
          isCustomParam,
        };
      });
    } else {
      typeParamsKeys.forEach((key: string) => {
        const serviceParam = serviceParams[key];
        if (!serviceParam) {
          return;
        }
        const {
          showInGraph = true,
          showInSysDiag = true,
          isCustomParam = false,
          code,
        } = serviceParam;
        if (!showInSysDiag) {
          return;
        }
        const measurementUnits: string = serviceParam
          ? serviceParam.data_unit_of_measurement
          : '';

        const name: string = serviceParam ? serviceParam.title : '';

        let enumData = null;
        if (serviceParam?.enum) {
          const typeKey = serviceParam?.enum;
          enumData = serviceParamTypes[typeKey];
        }

        // newParamsOrder.push([key, showInTable, showInList])
        paramsObject[key] = {
          code,
          name,
          // value: paramsMap[code].value,
          showInGraph,
          measurementUnits,
          enumData,
          showInList: false,
          showInTable: false,
          techInfo: serviceParam?.techInfo || null,
          isCustomParam,
        };
      });
      const filteredUserParams: any = [];
      const uniqueCodes: any = {};

      userParamsOrder.forEach((item: any) => {
        const [code, showInTable, showInList] = item;
        const serviceParam = serviceParams[code];
        if (!serviceParam) {
          return;
        }
        const {
          showInGraph = true,
          showInSysDiag = true,
          isCustomParam = false,
        } = serviceParam;
        if (!showInSysDiag) {
          return;
        }

        if (paramsObject[code]) {
          paramsObject[code].showInTable = showInTable;
          paramsObject[code].showInList = showInList;
        }

        uniqueCodes[code] = true;
        filteredUserParams.push(item);
      });

      const leftParams: any = [];
      Object.values(paramsObject).forEach(
        ({ code, showInTable, showInList }: any) => {
          if (uniqueCodes[code]) {
            return;
          }
          leftParams.push([code, showInTable, showInList]);
        }
      );

      newParamsOrder = [...filteredUserParams, ...leftParams];
    }

    return { newParamsOrder, paramsObject };
  };

  const fetchData = (
    selectedDate: any,
    selectedSystemId: any,
    noLoader: boolean = false
  ) => {
    if (!selectedDate || !selectedSystemId) {
      return;
    }
    stopPlayback(true);
    !noLoader && setIsLoading(true);

    const site = allSystems[selectedSystemId]?.site || '';
    const timeLimit = sitesFlags[site]?.numOfHourLimitSysDiag || 0;
    const currDate = new Date();
    let date = selectedDate || currDate;
    const allowedPastTime = currDate.getTime() - timeLimit * 60 * 60 * 1000;
    // if selected date is before allowed past time, set it to allowed past time
    date =
      !!timeLimit && date.getTime() < allowedPastTime
        ? new Date(allowedPastTime)
        : date;
    // start of day for the selected date
    const startOfDay = moment(date).startOf('day').toDate();
    // Apply time limit if necessary
    const startDate =
      !!timeLimit && startOfDay.getTime() < allowedPastTime
        ? new Date(allowedPastTime)
        : startOfDay;
    // Check if selected date is today
    const isToday = isSameDay(date, currDate);
    // Calculate UTC start and end times of the selected date (1 Day)
    let { startTime: start, endTime: end } = getUtcRange(
      startDate,
      startDate,
      timezone
    );
    // if today, end time should be current time instead of end of day
    if (isToday) {
      end = timezone
        ? moment().tz(timezone).utc().valueOf()
        : moment.utc().valueOf();
    }
    let showDeactivateMsg = false;
    if (!isToday && autoupdate) {
      setAutoupdate(false);
      setCounter(0);
      showDeactivateMsg = true;
    }

    sdkSystem
      .getSystemDiagData(selectedSystemId, start, end)
      .then((resp: any) => {
        const { indoors = {}, outdoors = {}, bsBoxes = {} } = resp;
        let indoorParams: any = {};
        let outdoorParams: any = {};
        let bsBoxParams: any = {};

        Object.values(indoors).forEach((unit: any, index: number) => {
          const { ranges } = unit;
          indoorParams = { ...indoorParams, ...ranges };
        });

        Object.values(outdoors).forEach((unit: any) => {
          const { ranges } = unit;
          outdoorParams = { ...outdoorParams, ...ranges };
        });

        Object.values(bsBoxes).forEach((unit: any, index: number) => {
          const { ranges } = unit;
          bsBoxParams = { ...bsBoxParams, ...ranges };
        });

        const userPref: any = getUserStorePreferences();
        const userPrefObj: any = userPref?.serviceRecentSystemParamsO;

        let systemUserPref =
          (userPrefObj && userPrefObj[selectedSystemId]) || {};
        const {
          indoors: userIndoor,
          outdoors: userOut,
          bsBoxes: userBs,
        } = systemUserPref;

        const { newParamsOrder: indoorsOrder, paramsObject: indoorParamsObj } =
          getParamsOrder(userIndoor, Object.keys(indoorParams));
        const {
          newParamsOrder: outdoorsOrder,
          paramsObject: outdoorParamsObj,
        } = getParamsOrder(userOut, Object.keys(outdoorParams));
        const { newParamsOrder: bsBoxesOrder, paramsObject: bsBoxParamsObj } =
          getParamsOrder(userBs, Object.keys(bsBoxParams));

        const jumpContext = jumpContextRef.current;
        if (jumpContext?.params?.length && jumpContext?.unitType) {
          const targetParamsObj =
            jumpContext.unitType === 'indoor'
              ? indoorParamsObj
              : jumpContext.unitType === 'outdoor'
              ? outdoorParamsObj
              : jumpContext.unitType === 'bsBox'
              ? bsBoxParamsObj
              : null;
          if (targetParamsObj) {
            // Jumped-in params replace the default selection outright, so the
            // table matches exactly what the user had visible in Unit Diag.
            const requestedCodes = new Set(jumpContext.params.map(String));
            Object.keys(targetParamsObj).forEach((code) => {
              const isRequested = requestedCodes.has(code);
              targetParamsObj[code].showInTable = isRequested;
              if (isRequested) {
                targetParamsObj[code].showInList = true;
              }
            });
          }
        }

        setSystemParams({
          type: 'all',
          indoors: indoorsOrder,
          outdoors: outdoorsOrder,
          bsBoxes: bsBoxesOrder,
          indoorsObj: indoorParamsObj,
          outdoorsObj: outdoorParamsObj,
          bsBoxesObj: bsBoxParamsObj,
        });

        setIndoorData(indoors);
        setOutdoorData(outdoors);
        setBsData(bsBoxes);

        const jumpTimestamp = jumpContext?.timestamp;
        const initialSelectedTime =
          jumpTimestamp && jumpTimestamp >= start && jumpTimestamp <= end
            ? jumpTimestamp
            : end;
        setSelectedTime(initialSelectedTime);
        setTime({ start, end });

        if (jumpContext?.unitId) {
          setHighlightUnitId(jumpContext.unitId);
        }
        // Jump context only seeds the very first load — clear it so a later
        // date/system change (or auto-refresh) never re-applies it.
        jumpContextRef.current = null;
      })
      .catch((err: any) => addMessage({ message: err.message }))
      .finally(async () => {
        setIsLoading(false);
        if (showDeactivateMsg) {
          await confirm({
            title: 'Note',
            bodyMessage:
              'Note you are moving from Auto Refresh to latest Data to viewing specific time. You can change back using the toggle to the right',
            confirmText: 'OK',
          });
        }
        if (autoupdate) {
          if (counter === 29) {
            setAutoupdate(false);
            setCounter(0);
            const ok = await confirm({
              title: 'Confirm',
              bodyMessage: 'Auto Update has stopped. Do you want to proceed?',
              confirmText: 'Yes',
              cancelText: 'No',
            });
            if (ok) {
              setLastUpdate();
            }
          } else {
            setCounter(counter + 1);
          }
        }
      });
  };

  useEffect(() => {
    if (!allSites || !allSystems || !allUnits || allObj.length) {
      return;
    }

    const sites = Object.values(allSites).map((site: any) => {
      return {
        name: site.name,
        label: site.name,
        type: 'site',
        id: site.id,
      };
    });

    const systems = Object.values(allSystems).map((system: any) => {
      const siteName = allSites[system.site]?.name;
      return {
        name: system.name,
        label: `${siteName} - ${system.name}`,
        type: 'system',
        id: system.id,
      };
    });
    const units: any = [];
    Object.values(allUnits).forEach((unit: any) => {
      if (unit.type === unitTypes.indoor) {
        return;
      }
      const siteName = allSites[unit.site]?.name;
      const systemName = allSystems[unit.system]?.name || t`Unassigned`;
      units.push({
        name: unit.name,
        label: `${siteName} - ${systemName} - ${unit.name}`,
        type: 'unit',
        id: unit.id,
      });
    });

    setAllObj([...sites, ...systems, ...units]);
  }, [allUnits]);

  useEffect(() => {
    setUnitUpdateStatus({ status: '' });
  }, []);

  const valuetext = (value: number) => {
      const timeToShow = moment(value).tz(timezone).format(timeFormat);
      return `${timeToShow}`;
    },
    valueLabelFormat = (value: number) => {
      const timeToShow = moment(value).tz(timezone).format(timeFormat);
      return `${timeToShow}`;
    };

  const stopPlayback = (fullReset: boolean = false) => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (fullReset) {
      setSeekDir(null);
      seekDirRef.current = null;
      setSeekMult(1);
      seekMultRef.current = 1;
    }
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastTsRef.current = null;
  };

  const tick = (ts: number) => {
    if (!isPlayingRef.current) return;
    if (lastTsRef.current === null) {
      lastTsRef.current = ts;
    }
    const dt = (ts - lastTsRef.current) / 1000;
    lastTsRef.current = ts;

    const mult = seekDirRef.current ? seekMultRef.current : 1;
    const speedMinutesPerSec = 5; // base 5 minutes of data per second
    const deltaMs = speedMinutesPerSec * 60 * 1000 * dt * mult;

    const current = selectedTimeRef.current || timeRef.current.start;
    let newTime = current;

    if (seekDirRef.current === 'bwd') {
      newTime = current - deltaMs;
      if (newTime <= timeRef.current.start) {
        newTime = timeRef.current.start;
        selectedTimeRef.current = newTime;
        setSelectedTime(newTime);
        stopPlayback();
        return;
      }
    } else {
      newTime = current + deltaMs;
      if (newTime >= timeRef.current.end) {
        newTime = timeRef.current.end;
        selectedTimeRef.current = newTime;
        setSelectedTime(newTime);
        stopPlayback();
        return;
      }
    }

    const rounded = Math.round(newTime);
    selectedTimeRef.current = rounded;
    setSelectedTime(rounded);
    rafIdRef.current = requestAnimationFrame(tick);
  };

  const startPlayback = () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    lastTsRef.current = null;
    setIsPlaying(true);
    isPlayingRef.current = true;
    rafIdRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  /**
   * Prompts to leave Auto Update. Returns true if the user confirmed (and
   * autoupdate has been switched off), or false if they cancelled — callers
   * must bail out without applying their action when this returns false.
   */
  const confirmExitAutoupdate = async () => {
    const ok = await confirm({
      title: t`Note`,
      bodyMessage:
        t`Note you are moving from Auto Refresh to latest Data to viewing specific time. You can change back using the toggle to the right`,
      confirmText: 'OK',
      cancelText: t`Cancel`,
    });
    if (!ok) {
      return false;
    }
    setAutoupdate(false);
    setCounter(0);
    return true;
  };

  const handlePlayPause = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    if (isPlaying) {
      stopPlayback(true);
    } else {
      setSeekDir(null);
      seekDirRef.current = null;
      setSeekMult(1);
      seekMultRef.current = 1;
      startPlayback();
    }
  };

  const handleFastFW = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    if (seekDir === 'fwd') {
      if (isPlaying) {
        const nextMult = seekMult === 2 ? 3 : seekMult === 3 ? 4 : 2;
        setSeekMult(nextMult);
        seekMultRef.current = nextMult;
      } else {
        startPlayback();
      }
    } else {
      setSeekDir('fwd');
      seekDirRef.current = 'fwd';
      setSeekMult(2);
      seekMultRef.current = 2;
      startPlayback();
    }
  };

  const handleFastRW = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    if (seekDir === 'bwd') {
      if (isPlaying) {
        const nextMult = seekMult === 2 ? 3 : seekMult === 3 ? 4 : 2;
        setSeekMult(nextMult);
        seekMultRef.current = nextMult;
      } else {
        startPlayback();
      }
    } else {
      setSeekDir('bwd');
      seekDirRef.current = 'bwd';
      setSeekMult(2);
      seekMultRef.current = 2;
      startPlayback();
    }
  };

  const handleJumpStart = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    stopPlayback(true);
    selectedTimeRef.current = time.start;
    setSelectedTime(time.start);
  };

  const handleJumpEnd = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    stopPlayback(true);
    selectedTimeRef.current = time.end;
    setSelectedTime(time.end);
  };

  const handleStepBack = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    stopPlayback(true);
    const newTime = Math.max(
      time.start,
      moment(selectedTime || time.start).subtract(1, 'minute').valueOf()
    );
    selectedTimeRef.current = newTime;
    setSelectedTime(newTime);
  };

  const handleStepForward = async () => {
    if (autoupdate && !(await confirmExitAutoupdate())) {
      return;
    }
    stopPlayback(true);
    const isToday = moment(
      moment(selections?.dateRange?.endDate).valueOf()
    ).isSame(moment().valueOf(), 'day');
    const newTime = Math.min(
      time.end,
      moment(selectedTime || time.start).add(1, 'minute').valueOf()
    );
    if (
      newTime === time.end &&
      !autoupdate &&
      isToday
    ) {
      const ok = await confirm({
        title: t`Confirm`,
        bodyMessage:
          t`Do you want to move to Auto Refresh to latest Data mode?`,
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (ok) {
        setLastUpdate();
      }
    }
    selectedTimeRef.current = newTime;
    setSelectedTime(newTime);
  };

  const handleSliderChange = (event: React.ChangeEvent<{}>, value: any) => {
    stopPlayback(true);
    if (autoupdate) {
      // Don't move the thumb at all while autoupdate is on — remember what
      // the user is dragging toward, but gate it behind the exit-autoupdate
      // confirmation instead of applying it live.
      pendingDragValueRef.current = value;
      if (!autoupdateGateRef.current) {
        autoupdateGateRef.current = true;
        confirmExitAutoupdate().then((ok) => {
          autoupdateGateRef.current = false;
          if (ok) {
            const finalValue = pendingDragValueRef.current;
            selectedTimeRef.current = finalValue;
            setSelectedTime(finalValue);
          }
          // Cancelled: do nothing — the thumb never moved, so there's
          // nothing to revert.
        });
      }
      return;
    }
    selectedTimeRef.current = value as number;
    setSelectedTime(value as number);
  };

  const handleSliderChangeCommitted = async (
    event: React.ChangeEvent<{}>,
    value: any
  ) => {
    if (autoupdate) {
      // Fully handled by the gate in handleSliderChange.
      return;
    }
    const isToday = moment(
      moment(selections?.dateRange?.endDate).valueOf()
    ).isSame(moment().valueOf(), 'day');
    if (value === time.end && isToday) {
      const ok = await confirm({
        title: t`Confirm`,
        bodyMessage:
          t`Do you want to move to Auto Refresh to latest Data mode?`,
        confirmText: t`Yes`,
        cancelText: t`No`,
      });
      if (ok) {
        setLastUpdate();
      }
    }
    selectedTimeRef.current = value as number;
    setSelectedTime(value as number);
  };

  const setLastUpdate = (e: any = '') => {
    e && e.preventDefault();
    stopPlayback(true);
    const activeupdate = autoupdate;
    setAutoupdate(!autoupdate);
    const isToday = moment(
      moment(selections?.dateRange?.endDate).valueOf()
    ).isSame(moment().valueOf(), 'day');
    let d1 = new Date();
    d1.setMinutes(d1.getMinutes() - 3);
    if (!isToday && !activeupdate) {
      fetchData(d1, selectedSystemId, true);
      updateSelections({ type: 'time', data: { startDate: d1, endDate: d1 } });
      return;
    }
    const currentMins = moment(d1.getTime()).format('hh:mm');
    const selectedMins = moment(selectedTime).format('hh:mm');
    if (isToday && !activeupdate && currentMins !== selectedMins) {
      const now = new Date();
      fetchData(now, selectedSystemId, true);
      updateSelections({
        type: 'time',
        data: { startDate: now, endDate: now },
      });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        handlePlayPause();
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        handleStepBack();
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        handleStepForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [autoupdate, isPlaying, seekDir, seekMult, selectedTime, time]);

  const onRefresh = () => {
    if (!selectedSystemId) {
      return;
    }
    if (
      moment(selectedTime).format('hh:mm') !==
      moment(new Date().getTime()).format('hh:mm')
    ) {
      fetchData(new Date(), selectedSystemId, true);
    }
  };

  const startAutoUpdate = () => {
    if (autoupdate && !callTimer) {
      setCallTimer(
        setTimeout(() => {
          onRefresh();
          clearTimeout(callTimer);
          setCallTimer(null);
        }, AUTOUPDATE_INTERVAL)
      );
    }
  };

  useEffect(() => {
    if (autoupdate) {
      startAutoUpdate();
    } else {
      clearTimeout(callTimer);
      setCallTimer(null);
    }
  }, [autoupdate, callTimer]);

  const searchComponent = (
    <Autocomplete
      id="combo-box-demo"
      options={allObj}
      forcePopupIcon={false}
      getOptionLabel={(option) => option.label || ''}
      groupBy={(option) => option.type}
      value={''}
      inputValue={searchTerm}
      classes={{
        clearIndicator: classes.searchIcon,
        popupIndicator: classes.searchIcon,
        popper: classes.autoCompletePoper,
        paper: classes.autoCompletePaper,
        listbox: classes.autoCompleteItem,
        noOptions: classes.autoCompleteItem,
        groupLabel: classes.autoCompleteGroup,
        groupUl: classes.autoCompleteGroupUl,
      }}
      onInputChange={(e: any, val: any) => {
        setSearchTerm(val);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t`Search...`}
          InputProps={{
            disableUnderline: true,
            classes: { root: classes.inputRoot },
            ...params.InputProps,
            endAdornment: <Search />,
          }}
        />
      )}
      onChange={(e: any, val: any) => {
        setSearchTerm('');
        if (!val) {
          return;
        }
        updateSelections({ type: val.type, data: val.id });
      }}
    />
  );

  if (!isInitialized) {
    return <Loading />;
  }

  return (
    <div className={classes.view}>
      <ServiceNavigationBar {...props} />
      <div className={classes.contentArea}>
        <Header
          onSystemSelect={(id: any) => {
            fetchData(selections.dateRange?.startDate, id);
            history.push(`/system-diagnostics/${id}`);
          }}
          onSiteSelect={() => history.push(`/system-diagnostics/`)}
          hideUnitSelection
          showOneDatePicker
          disableOneDatePicker={!displayFlags.enableSystemDiagnosticsDatepicker}
          customGeneralNames={{ system: t`Select System` }}
          searchComponent={searchComponent}
          minDate={
            !!timeLimit
              ? pastTimeAllowed > deviceRegDate
                ? new Date(pastTimeAllowed)
                : new Date(deviceRegDate)
              : new Date(deviceRegDate)
          }
          screenTitle="systemDiag"
          passSelectedDate={onDateChange}
        />
        {!selectedSystemId ? (
          <FilterRequire type={t`system`} />
        ) : isLoading ? (
          <div className={classes.progress}>
            <div className={classes.progressContainer}>
              <div className={clsx(classes.processingAnimationContainer)}>
                <CircularProgress />
              </div>
            </div>
          </div>
        ) : system &&
          (!_.isEmpty(outdoorData) ||
            !_.isEmpty(bsData) ||
            !_.isEmpty(indoorData)) ? (
          <>
            <div className={classes.timelineCard}>
                <div className={classes.timelineRow}>
                  <div className={classes.leftGroup}>
                    <div className={classes.timePill}>
                      <Typography className={classes.tdate}>
                        {valueLabelFormat(time.start)}
                      </Typography>
                      <div className={classes.sliderWrap}>
                        <Slider
                          className={classes.customSlider}
                          onChange={handleSliderChange}
                          onChangeCommitted={handleSliderChangeCommitted}
                          getAriaValueText={valuetext}
                          aria-labelledby="discrete-slider-always"
                          min={time.start}
                          max={time.end}
                          step={60 * 1000}
                          value={selectedTime || time.start}
                          valueLabelDisplay="on"
                          valueLabelFormat={valueLabelFormat}
                          ValueLabelComponent={ValueLabelComponent}
                        />
                      </div>
                      <Typography className={classes.tdate}>
                        {valueLabelFormat(time.end)}
                      </Typography>
                    </div>

                    <div className={classes.playerControls}>
                      <Tooltip
                        arrow
                        title={t`Jump to Start`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <button
                          type="button"
                          aria-label="Jump to start"
                          className={clsx(classes.playerBtn, classes.jumpBtn)}
                          onClick={handleJumpStart}
                          disabled={selectedTime <= time.start}
                        >
                          <PreviousIcon width={14} height={14} />
                        </button>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={t`1 Min Back`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <button
                          type="button"
                          aria-label="1 minute back"
                          className={clsx(classes.playerBtn, classes.jumpBtn)}
                          onClick={handleStepBack}
                          disabled={selectedTime <= time.start}
                        >
                          <StepBackIcon width={14} height={14} />
                        </button>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={t`Fast RW - click to increase speed (3 speeds available)`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                        PopperProps={{ modifiers: { flip: { enabled: false } } }}
                      >
                        <button
                          type="button"
                          aria-label="Fast rewind"
                          className={clsx(classes.playerBtn, classes.seekBtn)}
                          onClick={handleFastRW}
                        >
                          <RewindIcon width={32} height={32} />
                          {seekDir === 'bwd' && (
                            <span className={classes.seekBadge}>{seekMult}x</span>
                          )}
                        </button>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={isPlaying ? t`Pause` : t`Play`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <button
                          type="button"
                          aria-label="Play or pause"
                          className={clsx(classes.playerBtn, classes.playBtn)}
                          onClick={handlePlayPause}
                        >
                          {isPlaying ? (
                            <PauseIcon width={40} height={40} />
                          ) : (
                            <PlayIcon width={40} height={40} />
                          )}
                        </button>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={t`Fast FW - click to increase speed (3 speeds available)`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                        PopperProps={{ modifiers: { flip: { enabled: false } } }}
                      >
                        <button
                          type="button"
                          aria-label="Fast forward"
                          className={clsx(classes.playerBtn, classes.seekBtn)}
                          onClick={handleFastFW}
                        >
                          <ForwardIcon width={32} height={32} />
                          {seekDir === 'fwd' && (
                            <span className={classes.seekBadge}>{seekMult}x</span>
                          )}
                        </button>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={t`1 Min Forward`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <button
                          type="button"
                          aria-label="1 minute forward"
                          className={clsx(classes.playerBtn, classes.jumpBtn)}
                          onClick={handleStepForward}
                          disabled={selectedTime >= time.end}
                        >
                          <StepForwardIcon width={14} height={14} />
                        </button>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={t`Jump to End`}
                        placement="top"
                        classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                      >
                        <button
                          type="button"
                          aria-label="Jump to end"
                          className={clsx(classes.playerBtn, classes.jumpBtn)}
                          onClick={handleJumpEnd}
                          disabled={selectedTime >= time.end}
                        >
                          <NextIcon width={14} height={14} />
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  <div className={classes.divider} />

                  <InputLabel className={classes.autoLabel}>
                    {t`Auto Update`}
                    <CoolSwitch
                      checked={autoupdate}
                      onChange={setLastUpdate}
                    />
                  </InputLabel>
                </div>
              </div>
              <Grid
                container
                justifyContent="flex-start"
                alignItems="stretch"
                className={classes.wrapper}
              >
                <Grid item xs={2} className={classes.filterContainer}>
                  {!_.isEmpty(outdoorData) && (
                    <Grid
                      item
                      className={clsx(
                        classes.filterOutdoorGridItem,
                        !_.isEmpty(bsData) ? '' : classes.filterContainerBs
                      )}
                    >
                      <PropsFilter
                        systemId={selectedSystemId}
                        title={t`Outdoor Parameters`}
                        type={'outdoors'}
                        orderedParams={systemParams.outdoors}
                        paramsObj={systemParams.outdoorsObj}
                        setSystemParams={setSystemParams}
                        updateUserSystemPreferences={
                          updateUserSystemPreferences
                        }
                      />
                    </Grid>
                  )}
                  {!_.isEmpty(bsData) && (
                    <Grid item className={classes.filterBsGridItem}>
                      <PropsFilter
                        systemId={selectedSystemId}
                        title={t`BS Parameters`}
                        type={'bsBoxes'}
                        orderedParams={systemParams.bsBoxes}
                        paramsObj={systemParams.bsBoxesObj}
                        setSystemParams={setSystemParams}
                        updateUserSystemPreferences={
                          updateUserSystemPreferences
                        }
                      />
                    </Grid>
                  )}
                  {!_.isEmpty(indoorData) && (
                    <Grid
                      item
                      className={clsx(
                        classes.filterGridItem,
                        !_.isEmpty(bsData) ? '' : classes.filterGridItemBs
                      )}
                    >
                      <PropsFilter
                        systemId={selectedSystemId}
                        title={t`Indoor Parameters`}
                        type={'indoors'}
                        orderedParams={systemParams.indoors}
                        paramsObj={systemParams.indoorsObj}
                        setSystemParams={setSystemParams}
                        updateUserSystemPreferences={
                          updateUserSystemPreferences
                        }
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid item xs={10} className={classes.tableWrapper}>
                  {!_.isEmpty(outdoorData) && (
                    <Grid
                      item
                      className={clsx(
                        classes.tableOutdoorGridItem,
                        !_.isEmpty(bsData) ? classes.tableOutdoorGridItemBs : ''
                      )}
                    >
                      <ParamsTable
                        persistentFilters={true}
                        title={'Outdoor Dashboard'}
                        paramsData={outdoorData}
                        selectedTime={selectedTime}
                        isLast={isLast}
                        setIsLast={setIsLast}
                        orderedParams={systemParams.outdoors}
                        paramsObj={systemParams.outdoorsObj}
                        highlightUnitId={highlightUnitId}
                      />
                    </Grid>
                  )}

                  {!_.isEmpty(bsData) && (
                    <Grid item className={classes.tableBsGridItem}>
                      <ParamsTable
                        persistentFilters={true}
                        title={'BS Box Dashboard'}
                        paramsData={bsData}
                        selectedTime={selectedTime}
                        isLast={isLast}
                        setIsLast={setIsLast}
                        orderedParams={systemParams.bsBoxes}
                        paramsObj={systemParams.bsBoxesObj}
                        highlightUnitId={highlightUnitId}
                      />
                    </Grid>
                  )}
                  {!_.isEmpty(indoorData) && (
                    <Grid
                      item
                      className={clsx(
                        classes.tableGridItem,
                        !_.isEmpty(bsData) ? '' : classes.filterGridItemBs
                      )}
                    >
                      <ParamsTable
                        persistentFilters={true}
                        title={'Indoor Dashboard'}
                        paramsData={indoorData}
                        selectedTime={selectedTime}
                        isLast={isLast}
                        setIsLast={setIsLast}
                        orderedParams={systemParams.indoors}
                        paramsObj={systemParams.indoorsObj}
                        highlightUnitId={highlightUnitId}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
          </>
        ) : (
          <Grid
            container
            direction={'column'}
            className={classes.noContentContainer}
          >
            <Typography className={classes.noUnitText}>
              {t`No info about system`}
            </Typography>
          </Grid>
        )}
      </div>
    </div>
  );
};

export default SystemDiagnostics;
